<template>
  <div v-if="showSpinner" class="credit-cards">
    <LoadingSpinner />
  </div>
  <div v-else class="credit-cards">
    <NotificationsComponent context="CreditCardsComponent" />
    <div class="content">
      <KbSearchComponent />
      <h1>{{ tn("cardsOnFile") }}</h1>
      <ul class="card credit-card">
        <li v-for="card in creditCards" :id="`card${card.id}`" :key="card.id">
          <CardCreditComponent
            :card="card"
            :set-new-default-card="setNewDefaultCC"
            :remove-selected-credit-card="removeCreditCard"
            :show-remove-btn="creditCards.length > 1"
            :disable-remove-btn-for-default="creditCards.length > 2"
          />
        </li>
      </ul>
      <div>
        <button type="button" class="btn-primary" @click="handleAddCardClick">{{ tn("addCardBtn") }}</button>
        <button type="button" class="btn-link">{{ tn("editCardBtn") }}</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";

import { unifiedApi } from "@/common";
import { ICreditCard } from "@/common/api/unifiedPortal/interfaces";

import * as creditCardFunctions from "./CreditCards";

import KbSearchComponent from "@/components/shared/KbSearch.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import CardCreditComponent from "./CardCredit.vue";
import NotificationsComponent from "@/components/shared/Notifications.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";

const componentName = "CreditCardsComponent";

export default defineComponent({
  name: componentName,
  components: {
    KbSearchComponent,
    LoadingSpinner,
    CardCreditComponent,
    NotificationsComponent,
  },
  setup() {
    logEvent("created", componentName);

    const router = useRouter();
    const creditCards = ref<ICreditCard[]>([]);
    const showSpinner = ref<boolean>(true);

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    onMounted(async () => {
      try {
        const cards = (await unifiedApi.getUserCreditCards()).data;
        creditCards.value = cards ?? [];
      } catch (err) {
        handleApiError(err as AxiosError);
      }
      showSpinner.value = false;
    });

    async function setNewDefaultCC(creditCardId: string) {
      const cards = await creditCardFunctions.setDefaultCreditCard(creditCards.value, creditCardId);
      creditCards.value = cards ?? [];
    }

    async function removeCreditCard(creditCardId: string) {
      const cards = await creditCardFunctions.removeCreditCard(creditCards.value, creditCardId);
      creditCards.value = cards ?? [];
    }

    function handleAddCardClick() {
      router.push({ name: "add_credit_cards" });
    }

    //Expose to template
    return {
      tn,
      creditCards,
      showSpinner,
      setNewDefaultCC,
      removeCreditCard,
      handleAddCardClick,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
